import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

/*
   Four variants:

     primary      One per screen. The thing you came here to do.
                  Solid --brand at full saturation.
     default      Everything else. Unlimited per screen.
     ghost        Inside dense surfaces — table rows, drawer headers —
                  where a bordered button would add visual noise.
     destructive  OUTLINED red, never filled. Filled red belongs to
                  error severity; a delete button competing with a
                  finding's severity chip is a real misread.

   Disabled: each variant dims to about 55% opacity so the label stays
   readable and the brand identity is preserved. A greyed-out "Create"
   that renders as invisible material is a worse answer than a dimmed
   button — the reader needs to see the CTA to understand why it will
   not fire.
*/

export const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-1.5 shrink-0",
    "rounded-md border font-ui whitespace-nowrap select-none",
    "transition-colors duration-120 ease-out",
    "[&_svg]:shrink-0 [&_svg]:pointer-events-none",
    "disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none",
    "aria-busy:cursor-progress",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-brand border-brand !text-white font-semibold",
          "shadow-[0_1px_2px_rgb(200_70_26/0.30)]",
          "hover:bg-brand-strong hover:border-brand-strong",
          "active:bg-brand-strong",
        ],
        default: [
          "bg-surface border-border text-text-2 font-medium",
          "hover:bg-surface-sunken hover:border-border-strong hover:text-text",
          "active:bg-surface-sunken",
        ],
        ghost: [
          "bg-transparent border-transparent text-text-2 font-medium",
          "hover:bg-surface-sunken hover:text-text",
          "active:bg-surface-sunken",
        ],
        destructive: [
          "bg-surface border-err text-err font-medium",
          "hover:bg-err-tint",
          "active:bg-err-tint",
        ],
      },
      size: {
        sm: "h-7.5 px-2.5 text-body-sm [&_svg]:size-3.5",
        md: "h-9 px-3.5 text-body [&_svg]:size-4",
        lg: "h-10.5 px-4.5 text-body [&_svg]:size-4",
      },
      block: { true: "w-full", false: "" },
    },
    defaultVariants: { variant: "default", size: "md", block: false },
  },
)

type ButtonBaseProps = React.ComponentPropsWithoutRef<"button"> & VariantProps<typeof buttonVariants>

export interface ButtonProps extends ButtonBaseProps {
  /** Incompatible with `loading`, `leadingIcon`, `trailingIcon` — Slot needs one child. */
  asChild?: boolean
  loading?: boolean
  loadingText?: string
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    block,
    asChild = false,
    loading = false,
    loadingText,
    leadingIcon,
    trailingIcon,
    disabled,
    children,
    type = "button",
    ...props
  },
  ref,
) {
  if (import.meta.env?.DEV && asChild && (loading || leadingIcon || trailingIcon)) {
    // eslint-disable-next-line no-console
    console.warn(
      "[Button] `asChild` renders a single child, so `loading`, `leadingIcon` and `trailingIcon` are ignored.",
    )
  }

  const classes = cn(buttonVariants({ variant, size, block }), className)

  if (asChild) {
    return (
      <Slot ref={ref} className={classes} {...props}>
        {children}
      </Slot>
    )
  }

  const lead = loading ? (
    <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
  ) : (
    leadingIcon
  )

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {lead}
      {loading && loadingText ? <span aria-live="polite">{loadingText}</span> : children}
      {!loading && trailingIcon}
    </button>
  )
})

// ButtonGroup — joined peer actions on one object (Confirm / Dismiss / …)

export interface ButtonGroupProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string
}

export const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(function ButtonGroup(
  { className, label, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      role="group"
      aria-label={label}
      className={cn(
        "inline-flex items-center",
        "[&>button]:rounded-none [&>button+button]:-ml-px",
        "[&>button:first-child]:rounded-l-md [&>button:last-child]:rounded-r-md",
        "[&>button:hover]:relative [&>button:hover]:z-10",
        "[&>button:focus-visible]:relative [&>button:focus-visible]:z-10",
        className,
      )}
      {...props}
    />
  )
})
